<template>
  <div class="leave-report">
    <div class="leave-report-header">
      <section class="leave-report-name">
        <h2>Справка отпуск</h2>
        <div class="leave-report-tabs">
          <span
            v-for="t in tabs"
            :key="t.key"
            class="leave-report-tab"
            :class="{ 'leave-report-tab-active': tab === t.key }"
            @click="tab = t.key"
          >
            {{ t.name }}
          </span>
        </div>
      </section>

      <div class="header-controlls">
        <datepicker
          v-if="tab !== 'absences'"
          :language="bg"
          :minimum-view="'year'"
          :maximum-view="'year'"
          :format="'yyyy'"
          :input-class="'leave-report-date'"
          v-model="yearDate"
        ></datepicker>
        <template v-else>
          <datepicker
            :language="bg"
            :monday-first="true"
            :format="'dd.MM.yyyy'"
            :input-class="'leave-report-date'"
            v-model="fromDate"
          ></datepicker>
          <span>-</span>
          <datepicker
            :language="bg"
            :monday-first="true"
            :format="'dd.MM.yyyy'"
            :input-class="'leave-report-date'"
            v-model="toDate"
          ></datepicker>
        </template>
        <div class="btns">
          <button class="btn btn-excel" @click="exportToExcel()">
            <span class="miv">E</span>
            Експорт в Excel
          </button>
        </div>
      </div>
    </div>

    <!-- PAID / UNPAID BY MONTHS -->
    <template v-if="tab !== 'absences' && yearly">
      <p class="leave-report-info" v-if="tab === 'paid'">
        Платен отпуск: {{ yearly.year }} г., в работни дни (без събота, неделя
        и празници). Начален лимит {{ yearly.annual }} дни/год. (пропорционален при нов
        служител), неизползваните дни се пренасят до 2 години.
        <template v-if="yearly.year < yearly.startYear">
          Балансът се следи от {{ yearly.startYear }} г.
        </template>
      </p>
      <p class="leave-report-info" v-else>
        Неплатен отпуск: {{ yearly.year }} г., в работни дни (без събота,
        неделя и празници). Лимит {{ yearly.unpaidLimit }} работни дни/год.
      </p>

      <div class="table-wrapper" v-if="yearly.workers.length">
        <table class="leave-report-table">
          <thead>
            <tr>
              <th v-for="(h, i) in yearlyHeader" :key="`h-${i}`" :class="{ name: i === 1, summary: i > 13 }">
                {{ h }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, i) in yearlyRows"
              :key="`row-${i}`"
              :class="{ inactive: !yearly.workers[i].isActive }"
            >
              <td
                v-for="(c, j) in row"
                :key="`c-${i}-${j}`"
                :class="{
                  name: j === 1,
                  summary: j > 13,
                  empty: j > 1 && j < 14 && !c,
                  negative: j === row.length - 1 && c < 0,
                }"
              >
                {{ c === null ? "-" : c }}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td v-for="(c, j) in yearlyTotals" :key="`t-${j}`" :class="{ name: j === 1 }">
                {{ c }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
      <p v-else class="leave-report-empty">Няма служители за избраната година</p>
    </template>

    <!-- ABSENCES BY DATES -->
    <template v-if="tab === 'absences' && absences">
      <p class="leave-report-info">
        Одобрени отсъствия от {{ fromDate | formatDate }} до
        {{ toDate | formatDate }} в работни дни (без събота, неделя и празници).
        Командировките не са отсъствия.
      </p>
      <div class="table-wrapper" v-if="absences.workers.length">
        <table class="leave-report-table absences-table">
          <thead>
            <tr>
              <th>№</th>
              <th class="name">Служител</th>
              <th>Вид</th>
              <th>Дни</th>
              <th class="dates">Дати</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(w, i) in absences.workers">
              <tr v-for="(a, j) in w.absences" :key="`a-${w._id}-${a.type}`">
                <td v-if="j === 0" :rowspan="w.absences.length">{{ i + 1 }}</td>
                <td v-if="j === 0" :rowspan="w.absences.length" class="name">
                  {{ w.name }}
                </td>
                <td :class="`type type-${a.type}`">{{ typeNames[a.type] }}</td>
                <td>{{ a.count }}</td>
                <td class="dates">{{ formatDates(a.dates) }}</td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
      <p v-else class="leave-report-empty">Няма отсъствия за избрания период</p>
    </template>
  </div>
</template>

<script>
import Datepicker from "vuejs-datepicker";
import { bg } from "vuejs-datepicker/dist/locale";
import * as XLSX from "xlsx";
const MONTHS = ["Яну", "Фев", "Мар", "Апр", "Май", "Юни", "Юли", "Авг", "Сеп", "Окт", "Ное", "Дек"];
export default {
  components: {
    Datepicker,
  },
  data() {
    const now = new Date();
    return {
      bg: bg,
      tabs: [
        { key: "paid", name: "Платен отпуск" },
        { key: "unpaid", name: "Неплатен отпуск" },
        { key: "absences", name: "Отсъствия по дати" },
      ],
      tab: ["paid", "unpaid", "absences"].includes(this.$route.query.tab)
        ? this.$route.query.tab
        : "paid",
      yearDate: now,
      // the current month by default
      fromDate: new Date(now.getFullYear(), now.getMonth(), 1),
      toDate: new Date(now.getFullYear(), now.getMonth() + 1, 0),
      yearly: undefined,
      absences: undefined,
      typeNames: {
        paid: "Платен",
        unpaid: "Неплатен",
        hospital: "Болничен",
        fake: "Фиктивен-Неплатен",
        notpresent: "Неприсъствен",
      },
    };
  },
  computed: {
    year() {
      return new Date(this.yearDate).getFullYear();
    },
    period() {
      return `${this.toIso(this.fromDate)}/${this.toIso(this.toDate)}`;
    },
    yearlyHeader() {
      const header = ["№", "Служител"].concat(MONTHS, ["Използвани"]);

      if (this.tab === "paid") {
        return header.concat(["Разрешени", "Пренесени", "Общо", "Чакащи", "Остатък"]);
      }
      return header.concat(["Лимит", "Остатък"]);
    },
    // the same rows for the screen and the excel
    yearlyRows() {
      if (!this.yearly) return [];

      return this.yearly.workers.map((w, i) => {
        const l = w[this.tab];
        const row = [i + 1, w.name].concat(l.months, [l.used]);

        if (this.tab === "paid") {
          return l.tracked
            ? row.concat([l.allowed, l.carried, l.available, l.pending, l.remaining])
            : row.concat([null, null, null, l.pending, null]);
        }
        return row.concat([l.limit, l.remaining]);
      });
    },
    yearlyTotals() {
      const rows = this.yearlyRows;
      return this.yearlyHeader.map((h, j) => {
        if (j === 0) return "";
        if (j === 1) return "Общо";
        // limit/remaining are per person
        if (this.tab === "unpaid" && j > 14) return "";
        return rows.reduce((s, r) => s + (r[j] || 0), 0);
      });
    },
  },
  watch: {
    year() {
      this.getYearly();
    },
    period() {
      if (this.tab === "absences") this.getAbsences();
    },
    tab(t) {
      if (this.$route.query.tab !== t) {
        this.$router.replace({ query: { tab: t } });
      }
      if (t === "absences" && !this.absences) this.getAbsences();
    },
  },
  methods: {
    toIso(d) {
      d = new Date(d);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    formatDates(dates) {
      return dates
        .map((d) => {
          const [y, m, day] = d.split("-");
          return `${day}.${m}.${y}`;
        })
        .join(", ");
    },
    load(url, key) {
      this.$store.dispatch("startLoading");
      return this.$http
        .get(url)
        .then((response) => {
          this[key] = response.data;
        })
        .catch((err) => {
          console.log(err);
          this.$snotify.error(
            (err.response && err.response.data && err.response.data.failed) ||
              "Нещо се обърка"
          );
        })
        .then(() => this.$store.dispatch("stopLoading"));
    },
    getYearly() {
      this.load(
        `${process.env.VUE_APP_BASE_URI}/otpusk/report/year/${this.year}`,
        "yearly"
      );
    },
    getAbsences() {
      if (this.fromDate > this.toDate) {
        return this.$snotify.error("Началната дата не може да е след крайната!");
      }
      this.load(
        `${process.env.VUE_APP_BASE_URI}/otpusk/report/absences?from=${this.toIso(this.fromDate)}&to=${this.toIso(this.toDate)}`,
        "absences"
      );
    },
    exportToExcel() {
      let rows;
      let name;
      let cols;

      if (this.tab === "absences") {
        if (!this.absences || !this.absences.workers.length) {
          return this.$snotify.warning("Няма отсъствия за експорт");
        }
        rows = [["№", "Служител", "Вид", "Дни", "Дати"]];
        this.absences.workers.forEach((w, i) => {
          w.absences.forEach((a) => {
            rows.push([i + 1, w.name, this.typeNames[a.type], a.count, this.formatDates(a.dates)]);
          });
        });
        name = `Отсъствия-${this.formatDates([this.toIso(this.fromDate)])}-${this.formatDates([this.toIso(this.toDate)])}`;
        cols = [{ wch: 5 }, { wch: 30 }, { wch: 18 }, { wch: 6 }, { wch: 100 }];
      } else {
        if (!this.yearly || !this.yearly.workers.length) {
          return this.$snotify.warning("Няма служители за експорт");
        }
        rows = [this.yearlyHeader].concat(this.yearlyRows, [this.yearlyTotals]);
        name = `${this.tab === "paid" ? "Платен" : "Неплатен"}-отпуск-${this.year}`;
        cols = this.yearlyHeader.map((h, i) => ({ wch: i === 1 ? 30 : i < 2 ? 5 : 10 }));
      }

      const sheet = XLSX.utils.aoa_to_sheet(rows);
      sheet["!cols"] = cols;

      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "Справка отпуск");
      XLSX.writeFile(book, `${name}.xlsx`);
    },
  },
  mounted() {
    this.getYearly();
    if (this.tab === "absences") this.getAbsences();
  },
};
</script>

<style scoped>
.leave-report {
  background-color: #f0f7f7;
  min-height: calc(100vh - 100px);
  padding: 10px 40px;
}
.leave-report-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}
.leave-report-name {
  align-items: center;
  display: flex;
  gap: 20px;
}
.leave-report-tabs {
  display: flex;
  gap: 5px;
}
.leave-report-tab {
  background-color: #c9d9d9;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  padding: 8px 12px;
  transition: all 0.3s;
}
.leave-report-tab:hover {
  opacity: 0.8;
}
.leave-report-tab-active {
  background-color: #083f75;
  color: #fff;
  font-weight: 700;
}
.header-controlls {
  align-items: center;
  display: flex;
  gap: 10px;
}
.header-controlls .btns {
  display: flex;
}
.leave-report >>> .leave-report-date {
  background-color: #c9d9d9;
  border: 1px solid #c9d9d9;
  border-radius: 4px;
  cursor: pointer;
  height: 36px;
  padding: 0 10px;
  width: 120px;
}
.leave-report-info {
  font-size: 12px;
  margin: 0 0 10px;
}
.leave-report-empty {
  padding: 40px 0;
  text-align: center;
}
.table-wrapper {
  max-height: calc(100vh - 200px);
  overflow: auto;
}
.leave-report-table {
  background-color: #fff;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}
.leave-report-table th,
.leave-report-table td {
  border-bottom: 1px solid #c9d9d9;
  border-right: 1px solid #c9d9d9;
  min-width: 38px;
  padding: 5px 6px;
  text-align: center;
  white-space: nowrap;
}
.leave-report-table thead th {
  background-color: #e8eeee;
  font-weight: 700;
  position: sticky;
  top: 0;
}
.leave-report-table .name {
  min-width: 200px;
  text-align: left;
}
.leave-report-table .empty {
  color: #b0b0b0;
}
.leave-report-table .summary,
.leave-report-table thead .summary {
  background-color: #cfeefb;
}
.leave-report-table .negative {
  background-color: #ffc7ce;
  color: #9c0006;
  font-weight: 700;
}
.leave-report-table .inactive td {
  color: #8a8a8a;
}
.leave-report-table tfoot td {
  background-color: #deebf7;
  font-weight: 700;
}
.absences-table .dates {
  min-width: 300px;
  text-align: left;
  white-space: normal;
}
.absences-table .type-paid {
  background-color: #e6f3e6;
}
.absences-table .type-unpaid {
  background-color: #f3eae6;
}
.absences-table .type-hospital {
  background-color: #e6ecf3;
}
.absences-table .type-fake {
  background-color: #f3e99a;
}
.absences-table .type-notpresent {
  background-color: #e3e3e3;
}

@media screen and (max-width: 1000px) {
  .leave-report {
    padding: 10px 15px;
  }
  .leave-report-header,
  .leave-report-name {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }
  .header-controlls {
    flex-wrap: wrap;
  }
}
</style>
